import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../services/api';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import toast from 'react-hot-toast';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

interface InvoiceRow {
  date:           string;
  generation_kwh: number;
  rate_per_kwh:   number;
  amount:         number;
}

interface InvoiceReport {
  period_label: string;
  year:         number;
  month:        number;
  rate_per_kwh: number;
  rows:         InvoiceRow[];
  totals:       { total_kwh: number; total_amount: number };
}

export default function Invoice() {
  const [invYear,  setInvYear]  = useState(new Date().getFullYear());
  const [invMonth, setInvMonth] = useState(new Date().getMonth() + 1);
  const [rate,     setRate]     = useState<number>(4.0);
  const [downloading, setDownloading] = useState<string | null>(null);

  const { data: invData } = useQuery<InvoiceReport>({
    queryKey: ['report-invoice', invYear, invMonth, rate],
    queryFn:  () => reportsApi.invoice(invYear, invMonth, rate).then((r) => (r.data as { data: InvoiceReport }).data),
  });

  const handleDownload = async (fmt: string) => {
    setDownloading(fmt);
    try {
      const resp = await reportsApi.downloadInvoice(invYear, invMonth, rate, fmt);
      downloadBlob(new Blob([resp.data]), `invoice_${invYear}_${invMonth}.${fmt === 'pdf' ? 'pdf' : 'xlsx'}`);
      toast.success('Download started');
    } catch { toast.error('Download failed'); }
    finally { setDownloading(null); }
  };

  const periodFrom = format(startOfMonth(new Date(invYear, invMonth - 1)), 'dd MMM yyyy');
  const periodTo   = format(endOfMonth(new Date(invYear, invMonth - 1)), 'dd MMM yyyy');

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-bold text-slate-900">Generation Invoice</h1>

      <div className="card border border-emerald-200 bg-emerald-50">
        <p className="text-sm font-semibold text-emerald-900 mb-1">Shift Invoice</p>
        <p className="text-xs text-emerald-700">
          Calculate billing amount based on actual generation (kWh) and the configured tariff rate.
        </p>
      </div>

      {/* Controls */}
      <div className="card flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs text-slate-500 mb-1.5">Year</label>
          <select value={invYear} onChange={(e) => setInvYear(+e.target.value)} className="input w-24">
            {[2023, 2024, 2025, 2026].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1.5">Month</label>
          <select value={invMonth} onChange={(e) => setInvMonth(+e.target.value)} className="input w-28">
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1.5">Tariff Rate (Rs/kWh)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={rate}
            onChange={(e) => setRate(parseFloat(e.target.value) || 0)}
            className="input w-28"
          />
        </div>
        <div className="flex gap-2">
          <button onClick={() => handleDownload('excel')} disabled={!!downloading} className="btn-primary text-sm">
            {downloading === 'excel' ? 'Downloading…' : '⬇ Excel'}
          </button>
          <button onClick={() => handleDownload('pdf')} disabled={!!downloading} className="btn-ghost text-sm">
            {downloading === 'pdf' ? 'Downloading…' : '⬇ PDF'}
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {invData && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="card text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Billing Period</p>
              <p className="text-base font-bold text-slate-900 mt-2">{periodFrom}</p>
              <p className="text-xs text-slate-400">to {periodTo}</p>
            </div>
            <div className="card text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Total Generation</p>
              <p className="text-2xl font-bold font-mono text-blue-600 mt-2">{Number(invData.totals.total_kwh).toFixed(0)}</p>
              <p className="text-xs text-slate-400 mt-1">kWh</p>
            </div>
            <div className="card text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Tariff Rate</p>
              <p className="text-2xl font-bold font-mono text-slate-900 mt-2">Rs {rate.toFixed(2)}</p>
              <p className="text-xs text-slate-400 mt-1">per kWh</p>
            </div>
            <div className="card text-center bg-emerald-50 border border-emerald-200">
              <p className="text-xs text-emerald-700 uppercase tracking-wider font-semibold">Total Amount</p>
              <p className="text-2xl font-bold font-mono text-emerald-700 mt-2">
                Rs {Number(invData.totals.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-emerald-500 mt-1">Billable</p>
            </div>
          </div>

          <div className="card p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-hydro-border text-xs text-slate-500 uppercase tracking-wider">
                    {['Date', 'Generation (kWh)', 'Rate (Rs/kWh)', 'Amount (Rs)'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invData.rows.map((row) => (
                    <tr key={row.date} className="table-row text-xs">
                      <td className="px-4 py-2 font-mono">{format(new Date(row.date), 'dd MMM yyyy')}</td>
                      <td className="px-4 py-2 font-mono text-blue-600">{Number(row.generation_kwh).toFixed(0)}</td>
                      <td className="px-4 py-2 font-mono text-slate-500">{Number(row.rate_per_kwh).toFixed(2)}</td>
                      <td className="px-4 py-2 font-mono font-semibold text-emerald-700">
                        {Number(row.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-hydro-border bg-emerald-50 text-xs font-bold">
                    <td className="px-4 py-3 text-slate-700">TOTAL</td>
                    <td className="px-4 py-3 font-mono text-blue-700">{Number(invData.totals.total_kwh).toFixed(0)}</td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3 font-mono text-emerald-700">
                      {Number(invData.totals.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      {!invData && (
        <div className="card text-center text-slate-500 py-12">No data for selected period</div>
      )}
    </div>
  );
}
